import React from "react";
import {
  Alert,
  Card,
  Table,
  Tag,
  Typography,
} from "antd";

const { Title, Paragraph, Text } = Typography;

const standardFields = [
  {
    key: "name",
    field: "name",
    type: "string",
    required: "Yes",
    description: "Lead/customer name.",
    example: "John Doe",
  },
  {
    key: "phone",
    field: "phone",
    type: "string",
    required: "Yes",
    description:
      "Primary phone number. Used for duplicate detection.",
    example: "+971500001234",
  },
  {
    key: "email",
    field: "email",
    type: "string",
    required: "No",
    description: "Primary email address.",
    example: "john@example.com",
  },
  {
    key: "company",
    field: "company",
    type: "string",
    required: "No",
    description: "Company or institution name.",
    example: "Example LLC",
  },
  {
    key: "address",
    field: "address",
    type: "string",
    required: "No",
    description: "Lead address.",
    example: "Dubai, UAE",
  },
  {
    key: "country",
    field: "country",
    type: "string",
    required: "No",
    description: "Country.",
    example: "UAE",
  },
  {
    key: "profession",
    field: "profession",
    type: "string",
    required: "No",
    description: "Profession or designation.",
    example: "Sales Manager",
  },
  {
    key: "status_id",
    field: "status_id",
    type: "UUID",
    required: "No*",
    description:
      "Lead status UUID. If omitted during creation, the branch default status is used.",
    example: "STATUS_UUID",
  },
  {
    key: "source_id",
    field: "source_id",
    type: "UUID",
    required: "No",
    description:
      "Lead source UUID returned by GET /api/v1/meta.",
    example: "SOURCE_UUID",
  },
  {
    key: "purpose_id",
    field: "purpose_id",
    type: "UUID",
    required: "No",
    description:
      "Lead purpose UUID returned by GET /api/v1/meta.",
    example: "PURPOSE_UUID",
  },
  {
    key: "assigned_to",
    field: "assigned_to",
    type: "UUID",
    required: "No",
    description:
      "UUID of the CRM user assigned to the lead.",
    example: "USER_UUID",
  },
  {
    key: "custom_fields",
    field: "custom_fields",
    type: "array",
    required: "No",
    description:
      "Custom lead field values using field_id and value.",
    example: '[{"field_id":"UUID","value":"Value"}]',
  },
];

const customFieldTypes = [
  {
    key: "text",
    type: "text",
    value: "String",
    example: '"Interested in ERP"',
  },
  {
    key: "number",
    type: "number",
    value: "Number / numeric value",
    example: "5000",
  },
  {
    key: "dropdown",
    type: "dropdown",
    value: "Configured option value",
    example: '"High Priority"',
  },
  {
    key: "multi_select",
    type: "multi_select",
    value: "Multiple selected values",
    example: '["Option 1", "Option 2"]',
  },
  {
    key: "date",
    type: "date",
    value: "Date value",
    example: '"2026-07-20"',
  },
  {
    key: "time",
    type: "time",
    value: "Time value",
    example: '"14:30:00"',
  },
  {
    key: "datetime",
    type: "datetime",
    value: "Date and time value",
    example: '"2026-07-20 14:30:00"',
  },
];

const ApiFieldReference = () => {
  const columns = [
    {
      title: "Field",
      dataIndex: "field",
      width: 180,
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 120,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Required",
      dataIndex: "required",
      width: 100,
      render: (value) => (
        <Tag color={value === "Yes" ? "red" : "default"}>
          {value}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      minWidth: 300,
    },
    {
      title: "Example",
      dataIndex: "example",
      width: 260,
      render: (value) => (
        <Text code className="api-field-example">
          {value}
        </Text>
      ),
    },
  ];

  const customColumns = [
    {
      title: "Custom Field Type",
      dataIndex: "type",
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Expected Value",
      dataIndex: "value",
    },
    {
      title: "Example",
      dataIndex: "example",
      render: (value) => <Text code>{value}</Text>,
    },
  ];

  return (
    <div className="api-doc-section">
      <Title level={4}>Field Reference</Title>

      <Paragraph type="secondary">
        Reference for standard CRM lead fields and custom
        fields accepted by the Public API.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        message="Fetch branch-specific IDs first"
        description="Call GET /api/v1/meta to retrieve the valid status, source, purpose and custom-field IDs for the branch associated with your API key."
        style={{ marginBottom: 20 }}
      />

      <Card
        title="Standard Lead Fields"
        className="api-reference-card"
      >
        <Table
          rowKey="key"
          columns={columns}
          dataSource={standardFields}
          pagination={false}
          scroll={{ x: 1000 }}
          size="middle"
        />
      </Card>

      <Card
        title="Custom Fields"
        className="api-reference-card"
        style={{ marginTop: 20 }}
      >
        <Paragraph>
          Custom fields are submitted using the field UUID
          returned from <Text code>GET /api/v1/meta</Text>.
        </Paragraph>

        <pre className="api-code-block">
{`{
  "custom_fields": [
    {
      "field_id": "CUSTOM_FIELD_UUID",
      "value": "Custom value"
    },
    {
      "field_id": "MULTI_SELECT_FIELD_UUID",
      "value": ["Option 1", "Option 2"]
    }
  ]
}`}
        </pre>

        <Table
          rowKey="key"
          columns={customColumns}
          dataSource={customFieldTypes}
          pagination={false}
          scroll={{ x: 700 }}
        />
      </Card>

      <Card
        title="Reference Fields"
        className="api-reference-card"
        style={{ marginTop: 20 }}
      >
        <Paragraph>
          The following fields must reference records belonging
          to the same CRM branch as the API key:
        </Paragraph>

        <div className="api-reference-tags">
          <Tag>status_id</Tag>
          <Tag>source_id</Tag>
          <Tag>purpose_id</Tag>
          <Tag>assigned_to</Tag>
          <Tag>custom_fields.field_id</Tag>
        </div>

        <Alert
          type="warning"
          showIcon
          message="Do not send display names instead of IDs"
          description='For example, send "status_id": "UUID", not "status": "New". Reference IDs can be obtained from the metadata endpoint.'
          style={{ marginTop: 16 }}
        />
      </Card>
    </div>
  );
};

export default ApiFieldReference;